import { useMemo, useState } from 'react';
import type { Group, ToSendItem } from '../types';
import { daysUntil, formatShort, money } from '../utils';
import { isPlainClick } from '@/components/plainClick';

const ORDER: Group[] = ['needs_you', 'ready'];
const LABEL: Record<Group, string> = { needs_you: 'Needs you', ready: 'Ready to send' };

function Row({ item, selected, onSelect }: { item: ToSendItem; selected: boolean; onSelect: (id: string) => void }) {
  const codes = item.procedures.map((p) => p.code).join(', ');
  // Oct 22: an item waiting for an X-ray someone chose to add keeps its place with a neutral tag.
  const tag =
    item.waitingDoc === true
      ? { label: 'Waiting for the X-ray', cls: 'b-mute' }
      : item.group === 'needs_you'
        ? { label: 'Needs you', cls: 'b-warn' }
        : { label: 'Ready', cls: 'b-info' };
  const days = daysUntil(item.appointmentDate);
  const a11y = `${item.patient}, ${money(item.treatment)}, ${item.payer.short}, ${codes}, ${tag.label}: ${item.reasonLine}`;
  return (
    <a
      href={`/pre-auths/${item.id}`}
      className={`pa-qrow${selected ? ' pa-qsel' : ''}`}
      {...(selected ? { 'aria-current': 'true' as const } : {})}
      onClick={(e) => {
        if (!isPlainClick(e)) return;
        e.preventDefault();
        onSelect(item.id);
      }}
    >
      <span className="pa-vh">{a11y}</span>
      <div className="pa-qtop" aria-hidden="true">
        <span className="pa-qname">{item.patient}</span>
        <span className="pa-qamt num">{money(item.treatment)}</span>
      </div>
      <div className="pa-qsub" aria-hidden="true">
        {item.payer.short} · {codes}
      </div>
      <div className="pa-qtagline" aria-hidden="true">
        <span className={`pa-badge ${tag.cls}`}>{tag.label}</span>
        <span>{item.reasonLine}</span>
      </div>
      <div className="pa-qmeta" aria-hidden="true">
        Appointment {formatShort(item.appointmentDate)} · {days} days
      </div>
    </a>
  );
}

export function ToSendList({
  items,
  selectedId,
  onSelect,
}: {
  items: ToSendItem[];
  selectedId?: string;
  onSelect: (id: string) => void;
}) {
  const [query, setQuery] = useState('');

  const byGroup = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = items.filter((it) => {
      if (!q) return true;
      return (
        it.patient.toLowerCase().includes(q) ||
        it.payer.short.toLowerCase().includes(q) ||
        it.memberId.toLowerCase().includes(q) ||
        it.procedures.some((p) => p.code.toLowerCase().includes(q))
      );
    });
    const map = new Map<Group, ToSendItem[]>();
    for (const g of ORDER) map.set(g, []);
    for (const it of filtered) map.get(it.group)?.push(it);
    for (const arr of map.values()) arr.sort((a, b) => (a.appointmentDate < b.appointmentDate ? -1 : 1));
    // A waiting item keeps its group but sits after the ones that need a person.
    for (const arr of map.values()) arr.sort((a, b) => Number(a.waitingDoc === true) - Number(b.waitingDoc === true));
    return map;
  }, [items, query]);

  return (
    <aside className="pa-queue" aria-label="Pre-auths to send">
      <div className="pa-search-wrap">
        <label className="pa-vh" htmlFor="pa-search">
          Search pre-auths
        </label>
        <input id="pa-search" className="pa-search" placeholder="Search pre-auths" value={query} onChange={(e) => { setQuery(e.target.value); }} />
      </div>
      {ORDER.map((g) => {
        const rows = byGroup.get(g) ?? [];
        if (rows.length === 0) return null;
        return (
          <section key={g}>
            <h2 className="pa-group-head">
              {LABEL[g]} · {rows.filter((r) => r.waitingDoc !== true).length}
            </h2>
            {rows.map((it) => (
              <Row key={it.id} item={it} selected={it.id === selectedId} onSelect={onSelect} />
            ))}
          </section>
        );
      })}
    </aside>
  );
}
